import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Calendar, MessageCircle, Phone, Mail, ArrowRight, CheckCircle, Loader2, Linkedin } from 'lucide-react';
import { contactService } from '../services/api';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [statusMessage, setStatusMessage] = useState('');
  // Removed unused loading states for simplified approach

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await contactService.submitForm(formData);
      
      if (result.success) {
        setSubmitStatus('success');
        setStatusMessage(result.message);
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
        setStatusMessage(result.message);
      }
    } catch (error) {
      setSubmitStatus('error');
      setStatusMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
      // Clear status after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
        setStatusMessage('');
      }, 5000);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const openCalendly = () => {
    // Navigate to booking page
    window.location.href = '/book-consultation';
  };

  const openWhatsApp = () => {
    // Enhanced WhatsApp with consultation-specific message
    const message = `Hi Shunmugavel! 👋

I'm interested in your consulting services:
• Six Sigma Implementation
• ISO Certification & Audits  
• AI Management Systems
• Process Excellence

I'd like to discuss my requirements and schedule a consultation call.

Best regards`;
    
    const whatsappUrl = `https://wa.me/919962536379?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section className="dark-section-alt" id="contact" style={{ paddingTop: '20px' }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="display-medium mb-6">
            Ready to Transform Your <span className="turquoise-text">Business?</span>
          </h2>
          <p className="body-large max-w-2xl mx-auto">
            Let's discuss how we can drive excellence and compliance in your organization.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 px-4 lg:px-0">
          {/* Left side - Contact Network Image - Now visible on all screens */}
          <div className="lg:col-span-4 flex items-start max-h-[300px] md:max-h-[400px] lg:max-h-none overflow-hidden">
            <div className="relative group w-full lg:sticky lg:top-8">
              <img 
                src="/contact-network.png"
                alt="Contact Network - Let's Connect"
                className="w-full h-auto object-cover rounded-xl transform group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>

          {/* Right side - Contact Form and Info */}
          <div className="lg:col-span-8 space-y-8">
            {/* Contact Form */}
            <div className="dark-card">
              <h3 className="heading-2 mb-6">Get Started Today</h3>
              
              {/* Status Messages */}
              {submitStatus && (
                <div className={`mb-6 p-4 rounded-none border ${
                  submitStatus === 'success' 
                    ? 'bg-green-900/20 border-green-600 text-green-300'
                    : 'bg-red-900/20 border-red-600 text-red-300'
                }`}>
                  <div className="flex items-center gap-2">
                    {submitStatus === 'success' ? (
                      <CheckCircle size={20} />
                    ) : (
                      <Mail size={20} />
                    )}
                    <span className="body-medium">{statusMessage}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Your Name *"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="bg-transparent border-gray-600 text-white placeholder-gray-400"
                  />
                </div>
                
                <div>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    className="bg-transparent border-gray-600 text-white placeholder-gray-400"
                  />
                </div>
                
                <div>
                  <Input
                    type="text"
                    name="company"
                    placeholder="Company Name"
                    value={formData.company}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    className="bg-transparent border-gray-600 text-white placeholder-gray-400"
                  />
                </div>
                
                <div>
                  <Textarea
                    name="message"
                    placeholder="Tell us about your challenges and goals..."
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    rows={4}
                    className="bg-transparent border-gray-600 text-white placeholder-gray-400 resize-none"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Sending Message...
                    </>
                  ) : (
                    <>
                      <Mail size={20} />
                      Send Message
                      <ArrowRight size={20} />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Quick Actions and Direct Contact in Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="dark-card">
                <h3 className="heading-3 mb-6">Quick Actions</h3>
                
                <div className="space-y-3">
                  <Button 
                    onClick={openCalendly} 
                    className="btn-primary w-full"
                  >
                    <Calendar size={20} />
                    Schedule Call
                    <ArrowRight size={20} />
                  </Button>
                  
                  <Button 
                    onClick={openWhatsApp} 
                    className="btn-secondary w-full"
                  >
                    <MessageCircle size={20} />
                    WhatsApp
                    <ArrowRight size={20} />
                  </Button>
                  
                  <Button 
                    onClick={() => window.open('https://www.linkedin.com/in/shunmugavels', '_blank')}
                    className="btn-secondary w-full"
                  >
                    <Linkedin size={20} />
                    LinkedIn
                    <ArrowRight size={20} />
                  </Button>
                </div>
              </div>

              <div className="dark-card">
                <h3 className="heading-3 mb-6">Direct Contact</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Phone size={20} className="turquoise-text" />
                    <a href="tel:+919962536379" className="body-medium hover:turquoise-text transition-colors">
                      +91 9962536379
                    </a>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Mail size={20} className="turquoise-text" />
                    <a href="mailto:shunmugavelsubramanian@gmail.com" className="body-medium hover:turquoise-text transition-colors text-sm">
                      shunmugavelsubramanian@gmail.com
                    </a>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-gray-600">
                  <p className="body-small text-muted">
                    Response time: Within 24 hours
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;